import { zodResolver } from '@hookform/resolvers/zod';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { Controller, useForm } from 'react-hook-form';

import type { ArcLimits } from '@/api/generated';
import { Field } from '@/components/ui/Field';

import {
  isCounted,
  moveItemSchema,
  publishedCount,
  sectionLimit,
  visiblePublished,
  type MoveItemValues,
} from './arcSchemas';
import {
  inOrder,
  itemTitle,
  resourceTypeLabel,
  type ArcItem,
  type ArcSection,
} from './arcTypes';
import { FormDialog } from './FormDialog';

interface MoveItemDialogProps {
  item: ArcItem;
  from: ArcSection;
  sections: ArcSection[];
  /** The limits in force, from the reply. */
  limits: ArcLimits;
  /** Moves the item in the working copy; Save Draft saves it. */
  onApply: (sectionId: string) => void;
  onClose: () => void;
}

/** What the dialog weighs a target against: the item, where it is now, the limits. */
interface Moving {
  item: ArcItem;
  from: ArcSection;
  limits: ArcLimits;
  /** The published items in the visible sections now. */
  visibleTotal: number;
}

/** Why a section cannot take this item, or null when it can. */
function refusedBy(section: ArcSection, { item, from, limits, visibleTotal }: Moving) {
  if (!section.accepts.includes(item.resource_type)) {
    return `takes no ${resourceTypeLabel(item.resource_type).toLowerCase()}s`;
  }
  const already = section.items.some(
    (other) =>
      other.resource_type === item.resource_type &&
      other.resource_id === item.resource_id,
  );
  if (already) return 'already has it';
  // Only a published item counts, so only it can fill a section (B25).
  if (!isCounted(item)) return null;
  if (publishedCount(section) >= sectionLimit(section, limits)) return 'full';
  // Out of a hidden section into a visible one, it joins the ARC's total (B10).
  return !from.visible && section.visible && visibleTotal >= limits.max_items
    ? 'ARC full'
    : null;
}

/**
 * Moves content to the end of another section in the working copy, saved
 * with the draft: the keyboard-friendly way across sections, beside dragging
 * within one. Sections that cannot take it are listed, disabled, saying why.
 */
export function MoveItemDialog({
  item,
  from,
  sections,
  limits,
  onApply,
  onClose,
}: MoveItemDialogProps) {
  const targets = inOrder(sections).filter((section) => section.id !== from.id);
  const moving: Moving = { item, from, limits, visibleTotal: visiblePublished(sections) };
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<MoveItemValues>({
    resolver: zodResolver(moveItemSchema),
    defaultValues: { sectionId: '' },
  });

  const submit = handleSubmit(({ sectionId }) => {
    onApply(sectionId);
    onClose();
  });

  return (
    <FormDialog
      open
      title={`Move “${itemTitle(item)}”`}
      description={`From “${from.name}” to the end of another section. Save the draft to keep it.`}
      dirty={isDirty}
      pending={false}
      failure={null}
      submitLabel="Move"
      onSubmit={(event) => void submit(event)}
      onClose={onClose}
      maxWidth="xs"
      testId="arc-builder-move-item-dialog"
    >
      <Controller
        name="sectionId"
        control={control}
        render={({ field }) => (
          <Field
            label="Section"
            error={errors.sectionId?.message}
            testId="arc-builder-move-item-dialog-section"
          >
            <TextField select {...field} size="small" fullWidth>
              {targets.map((section) => {
                const why = refusedBy(section, moving);
                return (
                  <MenuItem
                    key={section.id}
                    value={section.id}
                    disabled={why !== null}
                    // A menu item keeps to one line; a long name wraps instead (B28).
                    sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}
                  >
                    {why ? `${section.name} (${why})` : section.name}
                  </MenuItem>
                );
              })}
            </TextField>
          </Field>
        )}
      />
    </FormDialog>
  );
}
